import { Button } from '@mui/material'
import { ProductMasterBatchJobOperationTypeEnum } from '@ulysses-inc/harami_api_client'
import { useCallback, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { useModalClosePreventionIsPending } from 'src/hooks/useModalClosePrevention'
import { ConfirmDialog } from '../../../../components/RecordsDeleteDrawer/components/ConfirmDialog/ConfirmDialog'
import { CsvFileUploadSection } from '../../../../components/RecordsDeleteDrawer/components/CsvFileUploadSection/CsvFileUploadSection'
import { useFileSelection } from '../../../../hooks/useFileSelection'
import { useMasterFileUpload } from '../../../hooks/useMasterFileUpload'

type Props = Readonly<{
  masterUuid: string
  closeDrawer: () => void
}>

export const useMasterDeleteRecords = ({ masterUuid, closeDrawer }: Props) => {
  const { showErrorSnackbar } = useSnackbar()
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const {
    fileInputRef,
    selectedFile,
    handleFileSelect,
    handleFileChange,
    resetFile,
  } = useFileSelection()

  const CsvFileUploadSectionEl = (
    <CsvFileUploadSection
      fileInputRef={fileInputRef}
      selectedFile={selectedFile}
      onFileSelect={handleFileSelect}
      onFileChange={handleFileChange}
    />
  )

  const { uploadFile, isProcessing } = useMasterFileUpload({
    masterUuid,
    onClose: closeDrawer,
    operationType: ProductMasterBatchJobOperationTypeEnum.Delete,
  })

  const handleClose = useCallback(() => {
    resetFile()
    closeDrawer()
  }, [resetFile, closeDrawer])

  const handleDrawerClose = useModalClosePreventionIsPending(
    handleClose,
    isProcessing,
  )

  const handleUploadClick = useCallback(() => {
    if (!selectedFile) {
      showErrorSnackbar('CSVファイルを選択してください')
      return
    }
    setConfirmDialogOpen(true)
  }, [selectedFile, showErrorSnackbar])

  const handleConfirmDelete = useCallback(() => {
    if (!selectedFile) return
    setConfirmDialogOpen(false)
    uploadFile(selectedFile)
    resetFile()
  }, [selectedFile, uploadFile, resetFile])

  const handleCancelConfirm = useCallback(() => {
    setConfirmDialogOpen(false)
  }, [])

  const ConfirmDialogEl = (
    <ConfirmDialog
      open={confirmDialogOpen}
      onConfirm={handleConfirmDelete}
      onCancel={handleCancelConfirm}
    />
  )

  const UploadButtonEl = (
    <Button
      type="button"
      variant="contained"
      color="error"
      onClick={handleUploadClick}
      disabled={isProcessing || !selectedFile}
    >
      {isProcessing ? 'アップロード中...' : '削除する'}
    </Button>
  )

  const CloseButtonEl = (
    <Button
      variant="contained"
      color="secondary"
      onClick={handleClose}
      disabled={isProcessing}
    >
      閉じる
    </Button>
  )

  return {
    handleDrawerClose,
    CsvFileUploadSectionEl,
    ConfirmDialogEl,
    UploadButtonEl,
    CloseButtonEl,
  } as const
}
